import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, MapPin, Tag, Plus, ArrowRight, Sparkle, FolderOpen } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AddItemModal from "@/components/AddItemModal";
import AuthedImage from "@/components/AuthedImage";
import DashboardSearchBar from "@/components/DashboardSearchBar";
import DemoModeRibbon from "@/components/DemoModeRibbon";
import { formatLocationPath } from "@/lib/locationPath";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ items: 0, locations: 0, categories: 0, tier: "free", limits: null });
  const [allItems, setAllItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [s, i, l] = await Promise.all([
      api.get("/stats"),
      api.get("/items"),
      api.get("/locations"),
    ]);
    setStats(s.data); setAllItems(i.data); setLocations(l.data);
  };

  useEffect(() => { load(); }, []);

  const tiles = [
    { Icon: Package, label: "Items", value: stats.items, to: "/items", color: "bg-terracotta-50 text-terracotta-600", testid: "stat-items" },
    { Icon: MapPin, label: "Locations", value: stats.locations, to: "/locations", color: "bg-sage-400/20 text-sage-600", testid: "stat-locations" },
    { Icon: Tag, label: "Categories", value: stats.categories, to: "/categories", color: "bg-ochre-400/30 text-ochre-600", testid: "stat-categories" },
  ];

  const firstName = (user?.name || "friend").split(" ")[0];

  return (
    <div className="space-y-10" data-testid="dashboard">
      <DemoModeRibbon />
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-sand-300 bg-white p-8 md:p-12 shadow-ambient"
      >
        <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-terracotta-50 blur-2xl opacity-70" />
        <div className="absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-sage-400/20 blur-2xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-terracotta-600">
            <Sparkle weight="fill" size={14} /> Today
          </span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stoneink-900">
            Hello, {firstName}.
          </h1>
          <p className="mt-3 text-stoneink-700 max-w-xl">
            Catalog one more thing today and you&apos;ll thank yourself next month. What did you just put away?
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              data-testid="dashboard-add-item-btn"
              onClick={() => setOpen(true)}
              className="h-12 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 transition-all active:scale-[0.98] shadow-ambient"
            >
              <Plus weight="bold" size={18} /> Add an item
            </button>
            <Link
              to="/locations"
              data-testid="dashboard-manage-locations"
              className="h-12 px-6 rounded-xl bg-white border border-sand-300 hover:border-ochre-500 hover:bg-sand-100 text-stoneink-900 font-medium flex items-center gap-2 transition-colors"
            >
              <MapPin weight="duotone" size={18} /> Manage locations
            </Link>
          </div>
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
        data-testid="dashboard-search-section"
      >
        <DashboardSearchBar />
      </motion.section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiles.map(({ Icon, label, value, to, color, testid }, idx) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.07 }}
          >
            <Link
              to={to}
              data-testid={testid}
              className="group block rounded-3xl border border-sand-300 bg-white p-6 shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className={`h-12 w-12 rounded-2xl grid place-items-center ${color}`}>
                  <Icon weight="duotone" size={24} />
                </div>
                <ArrowRight weight="bold" size={18} className="text-stoneink-500 group-hover:text-terracotta-500 group-hover:translate-x-1 transition-all" />
              </div>
              <div className="mt-5 font-heading text-4xl font-semibold text-stoneink-900">{value}</div>
              <div className="text-stoneink-500 text-sm mt-1">{label}</div>
            </Link>
          </motion.div>
        ))}
      </section>

      {/* Top locations by item count — between the stat tiles and Recently Added */}
      {(() => {
        if (!locations.length) return null;
        // Build descendant map once for rollup counts
        const childrenOf = new Map();
        locations.forEach((l) => {
          if (!l.parent_id) return;
          if (!childrenOf.has(l.parent_id)) childrenOf.set(l.parent_id, []);
          childrenOf.get(l.parent_id).push(l.id);
        });
        const descendants = (rootId) => {
          const ids = new Set();
          const stack = [rootId];
          while (stack.length) {
            const cur = stack.pop();
            (childrenOf.get(cur) || []).forEach((c) => {
              if (!ids.has(c)) { ids.add(c); stack.push(c); }
            });
          }
          return ids;
        };
        const ranked = locations
          .map((l) => {
            const directCount = allItems.filter((it) => it.location_id === l.id).length;
            const descIds = descendants(l.id);
            const nestedCount = allItems.filter((it) => descIds.has(it.location_id)).length;
            const subCount = (childrenOf.get(l.id) || []).length;
            return { ...l, totalCount: directCount + nestedCount, subCount };
          })
          .filter((l) => l.totalCount > 0 || l.subCount > 0)
          .sort((a, b) => b.totalCount - a.totalCount || b.subCount - a.subCount)
          .slice(0, 4);
        if (ranked.length === 0) return null;
        return (
          <section data-testid="dashboard-top-locations">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl text-stoneink-900 font-medium tracking-tight">Your busiest places</h2>
                <p className="text-stoneink-500 text-sm">Where most of your things actually live.</p>
              </div>
              <Link to="/locations" className="text-sm text-terracotta-600 hover:text-terracotta-700 font-medium" data-testid="see-all-locations">See all →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {ranked.map((l, i) => (
                <motion.div key={l.id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={`/items?location=${l.id}`}
                    data-testid={`top-location-${l.id}`}
                    className="group block rounded-3xl border border-sand-300 bg-white shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all overflow-hidden"
                  >
                    <div className="relative aspect-[4/3] bg-gradient-to-br from-sage-400/30 via-sand-200 to-terracotta-50">
                      {l.cover_image ? (
                        <AuthedImage path={l.cover_image} alt={l.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                      ) : (
                        <div className="absolute inset-0 grid place-items-center text-sage-600 opacity-60">
                          <MapPin size={36} weight="duotone" />
                        </div>
                      )}
                      {/* Dark bottom gradient so the overlay pill is always readable */}
                      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-stoneink-900/70 to-transparent pointer-events-none" />
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-stoneink-900 text-xs font-medium"
                          data-testid={`top-location-stats-${l.id}`}
                        >
                          <span className="font-semibold">{l.totalCount}</span> item{l.totalCount === 1 ? "" : "s"}
                          {l.subCount > 0 && (
                            <>
                              <span className="opacity-40">·</span>
                              <FolderOpen size={11} weight="duotone" />
                              <span><span className="font-semibold">{l.subCount}</span> spots</span>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="font-heading text-lg text-stoneink-900 font-medium truncate">{l.name}</div>
                      {l.description && <div className="text-xs text-stoneink-500 mt-0.5 truncate">{l.description}</div>}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        );
      })()}

      {stats.tier === "free" && stats.limits && (
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-sand-300 bg-white p-6 sm:p-8 shadow-ambient"
          data-testid="usage-card"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-terracotta-600">Free plan</span>
              <h3 className="mt-1 font-heading text-2xl font-medium text-stoneink-900">You&apos;re using {stats.items} of {stats.limits.items} items</h3>
              <p className="text-stoneink-500 text-sm mt-1">And {stats.locations}/{stats.limits.locations} locations. Photos, voice & QR are Pro features.</p>
            </div>
            <Link to="/pricing" data-testid="dashboard-upgrade-btn"
              className="h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 active:scale-[0.98]">
              <Sparkle weight="fill" size={16} /> Upgrade to Pro
            </Link>
          </div>
          <div className="mt-5 space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs text-stoneink-500 mb-1">
                <span>Items</span><span>{stats.items}/{stats.limits.items}</span>
              </div>
              <div className="h-2 rounded-full bg-sand-200 overflow-hidden">
                <div className="h-full bg-terracotta-500 transition-all" style={{ width: `${Math.min(100, (stats.items / stats.limits.items) * 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-xs text-stoneink-500 mb-1">
                <span>Locations</span><span>{stats.locations}/{stats.limits.locations}</span>
              </div>
              <div className="h-2 rounded-full bg-sand-200 overflow-hidden">
                <div className="h-full bg-ochre-500 transition-all" style={{ width: `${Math.min(100, (stats.locations / stats.limits.locations) * 100)}%` }} />
              </div>
            </div>
          </div>
        </motion.section>
      )}

      <section>
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl text-stoneink-900 font-medium tracking-tight">Recently added</h2>
            <p className="text-stoneink-500 text-sm">Your latest treasures, snug in their spots.</p>
          </div>
          <Link to="/items" className="text-sm text-terracotta-600 hover:text-terracotta-700 font-medium" data-testid="see-all-items">See all →</Link>
        </div>
        {(() => {
          const recent = allItems.slice(0, 6);
          return recent.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-sand-300 bg-sand-100 p-10 text-center">
              <div className="text-stoneink-700">Nothing here yet.</div>
              <button onClick={() => setOpen(true)} className="mt-4 text-terracotta-600 font-medium hover:text-terracotta-700" data-testid="empty-add-first">
                Add your first item →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recent.map((it, i) => {
                const path = formatLocationPath(locations, it.location_id);
                return (
                  <motion.div key={it.id}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  >
                    <Link
                      to={`/items/${it.id}`}
                      data-testid={`recent-item-${it.id}`}
                      className="block rounded-3xl border border-sand-300 bg-white p-5 shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all"
                    >
                      <div className="font-heading text-lg text-stoneink-900 font-medium truncate">{it.name}</div>
                      <div className="text-sm text-stoneink-500 mt-1 truncate" title={path}>
                        {path ? `In ${path}` : "No location yet"}
                      </div>
                      {it.tags?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {it.tags.slice(0, 3).map((t) => (
                            <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-sand-200 text-stoneink-700">{t}</span>
                          ))}
                        </div>
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          );
        })()}
      </section>

      <AddItemModal open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}
